
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import axios, { AxiosError } from "axios";
import { MOCK_PRODUCTS } from '../data/marketplace/products';
import { MOCK_SUI_ESCROWS, MOCK_SOL_ESCROWS } from '../data/escrow/mainnet';
import { Company, Merchant, Client, Sale, UserRoleAssignment } from '../types/enterprise';
import { Transaction, ApiKey, Role } from '../types';

const API_DOMAIN = "https://api.ghostpay.fun";
const API_VERSION = "/api/v1";

const api = axios.create({
    baseURL: API_DOMAIN, 
    timeout: 10000, 
    headers: {
        'X-GHOST-OS': 'v2.1.0-STABLE',
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Global Error Interceptor
api.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
        const status = error.response?.status;
        if (status === 401) {
            console.error("[API] Unauthorized session. Identity verification required.");
        } else if (status === 429) {
            console.error("[API] Rate limit exceeded on GhostRelay.");
        }
        return Promise.reject(error);
    }
);

export async function fetchWithFallback<T>(endpoint: string, fallback: T, postData?: any): Promise<T> {
    try {
        const response = postData 
            ? await api.post<T>(endpoint, postData) 
            : await api.get<T>(endpoint);
        return response.data;
    } catch (error) {
        console.debug(`[API] Fallback triggered for ${endpoint}. Mocking data.`);
        return fallback;
    }
}

export const ENDPOINTS = {
    NFT: {
        VERIFY: `${API_VERSION}/nft/verify`,
        MINT: `${API_VERSION}/nft/mint`
    },
    POS: {
        TRANSFER: (asset: string) => `${API_VERSION}/pos/transfer/${asset}`
    },
    STORE: {
        ORDER: `${API_VERSION}/store/order`,
        PAY: (orderId: string) => `${API_VERSION}/store/pay/${orderId}`
    }
};

// --- MODULE: AUTH & KEYS ---
export const fetchApiKeys = () => fetchWithFallback(`${API_VERSION}/auth/keys`, [
    { id: 'key_1', name: 'Production POS', prefix: 'sk_live_51Mx', scopes: ['read:payments', 'write:payments'], chain: 'SOL', rateLimit: 5000, usage: 1240, createdAt: '2024-01-15', lastUsed: 'Just now', status: 'active' },
    { id: 'key_2', name: 'AI Research Agent', prefix: 'sk_live_92Bz', scopes: ['read:analytics', 'x402:pay'], chain: 'ALL', rateLimit: 1000, usage: 85, createdAt: '2024-02-10', lastUsed: '5m ago', status: 'active' }
] as ApiKey[]);

export const createApiKey = (data: any) => fetchWithFallback(`${API_VERSION}/auth/keys/generate`, { 
    ...data, 
    id: `key_${Date.now()}`, 
    prefix: `sk_live_${Math.random().toString(36).substring(7)}`,
    createdAt: new Date().toISOString(),
    status: 'active' 
} as ApiKey, data);

export const rotateApiKey = (id: string) => fetchWithFallback(`${API_VERSION}/auth/keys/${id}/rotate`, { success: true }, {});
export const revokeApiKey = (id: string) => fetchWithFallback(`${API_VERSION}/auth/keys/${id}/revoke`, { success: true }, {});

// --- MODULE: WALLET & LEDGER ---
export const getWalletBalances = (address?: string) => {
    const seed = address ? address.split('').reduce((a, b) => a + b.charCodeAt(0), 0) : 100;
    const isSui = address?.startsWith('SUI');
    const isSol = address?.startsWith('SOL');

    const mockBalances = {
        SUI: isSui ? (seed % 1000) + 120.5 : (seed % 100) + 5,
        GHOST: (seed % 5000) + 1200,
        SOL: isSol ? (seed % 50) + 12.4 : (seed % 10) + 0.5,
        USDC: (seed % 10000) + 500,
        CETUS: (seed % 2000) + 100,
        SPL: (seed % 300) + 10
    };

    return fetchWithFallback(`${API_VERSION}/wallet/balances?address=${address}`, mockBalances);
};

export const fetchTransactions = (walletAddress: string, page = 1, limit = 10) => {
    const seed = walletAddress.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
    const mockTxs: Transaction[] = Array.from({ length: 40 }, (_, i) => {
        const type = (['payment', 'swap', 'deposit', 'reward', 'escrow', 'withdrawal'] as const)[(seed + i) % 6];
        const asset = (['SOL', 'SUI', 'USDC', 'GHOST', 'CETUS'] as const)[(seed + i) % 5];
        const chain = asset === 'SUI' || asset === 'CETUS' ? 'SUI' : 'SOL';
        const amount = parseFloat(((seed % 50) + (i * 1.5) + (Math.random() * 10)).toFixed(2));
        
        return {
            id: `GHT_${seed + i}`,
            type,
            amount,
            status: i === 0 ? 'pending' : (i % 15 === 0 ? 'failed' : 'completed'),
            date: Date.now() - (i * 3600000 * 2) - (Math.random() * 1000000),
            asset,
            chain,
            txHash: chain === 'SUI' ? `0x${Math.random().toString(16).slice(2, 42)}` : `${Math.random().toString(36).slice(2, 44)}`,
            explorerUrl: chain === 'SUI' ? 'https://suiscan.org/mainnet/tx/' : 'https://solscan.io/tx/',
            recipient: i % 2 === 0 ? '8x92...3k1' : undefined,
            sender: i % 2 !== 0 ? '4k21...9x2' : undefined
        };
    });

    return fetchWithFallback(`${API_VERSION}/wallet/transactions?address=${walletAddress}&page=${page}&limit=${limit}`, { 
        transactions: mockTxs.slice((page - 1) * limit, page * limit), 
        total: mockTxs.length 
    });
};

// --- MODULE: COMMERCE ---
export const fetchProducts = () => fetchWithFallback(`${API_VERSION}/marketplace/products`, MOCK_PRODUCTS);

export const fetchEscrows = (address: string) => {
    const dynamicMocks = [...MOCK_SUI_ESCROWS, ...MOCK_SOL_ESCROWS].map(escrow => ({
        ...escrow,
        buyer: (escrow.chain === 'SUI' && address.startsWith('SUI')) ? address : escrow.buyer,
        seller: (escrow.chain === 'SOL' && address.startsWith('SOL')) ? address : escrow.seller
    }));
    
    return fetchWithFallback(`${API_VERSION}/escrow/list?address=${address}`, dynamicMocks);
};

export const createEscrow = (data: any) => fetchWithFallback(`${API_VERSION}/escrow/create`, { escrowId: `esc_${Date.now()}`, success: true }, data);

export const fetchPOSPayments = () => fetchWithFallback(`${API_VERSION}/pos/payments`, []);
export const fetchMarketplaceOrders = () => fetchWithFallback(`${API_VERSION}/marketplace/orders`, []);
export const fetchAIInsights = () => fetchWithFallback(`${API_VERSION}/ai/insights`, { alerts: [] });

// --- MODULE: STOREFRONT ---
export const createStoreOrder = (data: any) => fetchWithFallback(ENDPOINTS.STORE.ORDER, { orderId: `order_${Date.now()}`, success: true }, data);
export const payStoreOrder = (orderId: string, chain: string, amount: number) => fetchWithFallback(ENDPOINTS.STORE.PAY(orderId), { success: true }, { chain, amount });

// --- MODULE: ENTERPRISE ---
export const fetchCompanies = () => fetchWithFallback(`${API_VERSION}/enterprise/companies`, [] as Company[]);
export const fetchCompanyById = (id: string) => fetchWithFallback(`${API_VERSION}/enterprise/companies/${id}`, undefined as Company | undefined);
export const createCompany = (company: Omit<Company, 'id'>) => fetchWithFallback(`${API_VERSION}/enterprise/companies`, { ...company, id: `comp_${Date.now()}` } as Company, company);

export const fetchMerchants = () => fetchWithFallback(`${API_VERSION}/enterprise/merchants`, [] as Merchant[]);
export const fetchMerchantById = (id: string) => fetchWithFallback(`${API_VERSION}/enterprise/merchants/${id}`, undefined as Merchant | undefined);

export const fetchClients = () => fetchWithFallback(`${API_VERSION}/enterprise/clients`, [] as Client[]);
export const fetchClientById = (id: string) => fetchWithFallback(`${API_VERSION}/enterprise/clients/${id}`, undefined as Client | undefined);

export const fetchSales = () => fetchWithFallback(`${API_VERSION}/enterprise/sales`, [] as Sale[]);

export const fetchRoles = (companyId: string) => fetchWithFallback(`${API_VERSION}/enterprise/companies/${companyId}/roles`, [] as UserRoleAssignment[]);
export const assignRole = (companyId: string, userId: string, role: Role) => fetchWithFallback(`${API_VERSION}/enterprise/companies/${companyId}/roles/assign`, { userId, role } as UserRoleAssignment, { userId, role });
